import { func, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { emailRegex } from "@utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ControlledTextField } from "@components";
import { CircularProgress } from "@material-ui/core";

const passwordResetFormSchema = (t) =>
  yup.object().shape({
    email: yup
      .string()
      .matches(emailRegex, t("errors.email.required"))
      .required(t("errors.email.required")),
    password: yup
      .string()
      .matches(
        /^(?=.*[A-Z])(?=.*[!@#$&*.\\/"'%()+,\-:;<>?[\]^_{}|~])(?=.*[0-9])(?=.*[a-z]).{8,128}$/,
        t("errors.password.pattern")
      )
      .required(t("errors.password.required"))
      .min(8, t("errors.password.min"))
      .max(128, t("errors.password.max")),
    code: yup.string().required(t("errors.code.generic")).max(64),
  });

export const PasswordResetForm = ({ onSubmit, defaultEmail }) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(passwordResetFormSchema(t)),
    defaultValues: {
      email: defaultEmail || "",
      code: "",
      password: "",
    },
  });

  const submitHandler = async (data) => {
    const result = await onSubmit(data);

    if (result) {
      const { errors } = result;

      errors?.forEach(({ field, message }) => {
        setError(field, { message });
      });
    }
  };

  return (
    <form
      autoComplete="on"
      aria-label="password reset form"
      onSubmit={handleSubmit(submitHandler)}
      className="mt-1"
    >
      <ControlledTextField
        name="email"
        control={control}
        label={t("forms.email")}
        autoComplete="email"
        type="email"
        disabled={!!defaultEmail}
        fullWidth
      />

      <ControlledTextField
        name="code"
        control={control}
        label={t("forms.code")}
        autoComplete="email"
        fullWidth
      />

      <ControlledTextField
        name="password"
        type="password"
        control={control}
        label={t("forms.password.generic")}
        fullWidth
      />

      <Button
        isSubmit
        name="submit"
        variant="contained"
        fullWidth
        className="mt-6"
        endIcon={
          isSubmitting ? (
            <CircularProgress
              aria-label="progress indicator"
              size={24}
              color="inherit"
            />
          ) : undefined
        }
      >
        {t("forms.password.reset")}
      </Button>
    </form>
  );
};

PasswordResetForm.propTypes = {
  onSubmit: func.isRequired,
  defaultEmail: string,
};
